import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BusinessUnitModel } from '../../shared/models/business-unit/business-unit.model';
import { ACSTransactionType } from '../models/acs-transaction-type.enum';
import { OAuthData } from '../models/oauthdata.model';
import { SecurityClaim } from '../models/security-claim.model';
import { SecurityTransactionType } from '../models/security-transaction-type.enum';
import { SecurityCacheService } from './security-cache.service';
import { MsalBroadcastService, MsalService, } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { appInjector } from '../../shared/app-injector';
import { AppSettings } from '../../shared/settings/app-settings.model';

@Injectable()
/**
 * This is the service to be used throughout the application. It wraps the actual security implementation, which is currently adal (Azure AD).
*/
export class AuthService {

    private _renewTokenIntervalId: any = -1;
    public appSettings: AppSettings;
    private hasAuthErrors = false;
    private updatedAccessToken: string = null;

    /**
    * called when security stamps are updated
    */
    securityTransactionsUpdated: BehaviorSubject<SecurityClaim[]>;
    acsTransactionsUpdated: BehaviorSubject<SecurityClaim[]>;
    multipleClaimSetsUpdated: BehaviorSubject<SecurityClaim[]>;


    /**
     * MSAL authentication error codes
     */
    private readonly _authErrorCodes: any = {
        registrationCancelled: 'AADB2C90091',
        sessionExpired: 'AADB2C90077'
    };

    /**
     * Is used when the user changes business unit
    */
    businessUnitUpdated: BehaviorSubject<BusinessUnitModel>;

    /**
     * notification count should change based on bu
     * when the user changes business unit
    */
    notificationsCount: BehaviorSubject<string>;

    /**
     * return id token
    */
    idTokenUpdated: BehaviorSubject<string>;

    /**
     * return user name
    */
    userUpdated: BehaviorSubject<string>;

    /**
     * return security stamp
    */
    securityStampUpdated: BehaviorSubject<string>;

    /**
     * The Authorization header name
    */
    public get securityStampHeaderName(): string {
        return 'SecurityStamp';
    }

    /**
    * The Authorization header name
    */
    public get businessUnitHeaderName(): string {
        return 'BUID';
    }

    /**
    * The Authorization header name
    */
    public get windowHeaderName(): string {
        return 'WindowId';
    }

    /**
     * Gets the API scopes for the application.
     */
    private get scopes() {
        return [this.appSettings.security.scope];
    }

    /**
    * Indicates whether the currently logged in user has a valid selected business unit
    */
    get hasBusinessUnitAccess(): boolean {
        var businessUnit: string = SecurityCacheService.businessUnitId;
        return this.isAuthenticated && businessUnit != undefined && businessUnit.length > 0;
    }

    /**
     * Determines whether the current user is authenticated.
     */
    get isAuthenticated() {
        return !!this._msalService.instance.getActiveAccount();
    }

    /**
     * Determines weather there are authentication errors.
     */
    public hasAuthenticationErrors() {
        return this.hasAuthErrors;
    }

    /**
     * Returns the currently cached access token
    */
    get accessToken() {
        return this.updatedAccessToken;
    }

    /**
     * The user's name
    */
    get name() {
        return this._msalService.instance.getActiveAccount()?.name;
    }

    /**
     * The user's login name
    */
    get userName() {
        return this._msalService.instance.getActiveAccount()?.username;
    }

    /**
     * The currently selected business unit id
    */
    get businessUnitId() {
        return SecurityCacheService.businessUnitId;
    }

    /**
     * The currently selected business unit type id
    */
    get businessUnitTypeId() {
        return SecurityCacheService.businessUnitTypeId;
    }

    /**
     * The current security stamp
    */
    get securityStamp() {
        return SecurityCacheService.securityStamp;
    }

    /**
     * The currently selected business unit name
    */
    get businessUnitName() {
        return SecurityCacheService.businessUnitName;
    }

    /**
     * Returns whether the user is properly authenticated
    */
    get validUser() {
        return this.currentUser ? this.currentUser.isAuthenticated : false;
    }

    /**
     * The prefix for a security transaction claim.
    */
    get transactionClaim() {
        return `${this.appSettings.security.resourceIdentifier}:trx`;
    }

    /**
    * The prefix for an acs transaction claim.
    */
    get acsTransactionClaim() {
        return `${this.appSettings.security.resourceIdentifier}:accessControl`;
    }

    /**
    * The prefix for multipleClaimSet
    */
    get multipleClaimSet() {
        return `${this.appSettings.security.resourceIdentifier}:multipleClaimSet`;
    }

    /**
     * Checking security stamp of the request
     */
    get hasSecurityStamp() {
        return !!this.securityStamp && this.securityStamp.length > 0;
    }

    /**
     * Indicates whether the currently logged in user has been properly authenticated
    */
    get hasApplicationAccess(): boolean {
        return this.isAuthenticated;
    }

    /**
     * All of the user data and profile data
     */
    get currentUser(): OAuthData {
        return {
            isAuthenticated: this.isAuthenticated,
            userName: this.userName,
            name: this.name,
            loginError: '',
            profile: this._msalService.instance.getActiveAccount()
        };
    }

    constructor(
        private _router: Router,
        private _msalService: MsalService,
        private _msalBroadcastService: MsalBroadcastService,
    ) {
        const cachedId: string = this.businessUnitId;
        const cachedName: string = this.businessUnitName;

        const businessUnit = new BusinessUnitModel();

        if (cachedId != undefined && cachedId.length > 0) {
            businessUnit.id = cachedId as any;
        }
        businessUnit.businessUnitName = cachedName;

        this.businessUnitUpdated = new BehaviorSubject<BusinessUnitModel>(businessUnit);
        this.idTokenUpdated = new BehaviorSubject<string>('');
        this.userUpdated = new BehaviorSubject<string>('');
        this.securityStampUpdated = new BehaviorSubject<string>('');
        this.securityTransactionsUpdated = new BehaviorSubject<SecurityClaim[]>(null);
        this.acsTransactionsUpdated = new BehaviorSubject<SecurityClaim[]>(null);
        this.multipleClaimSetsUpdated = new BehaviorSubject<SecurityClaim[]>(null);
        this.notificationsCount = new BehaviorSubject<string>('');

        // get all of the security settings from AppSettings in the security section
        var injector = appInjector();
        this.appSettings = <AppSettings>injector.get(AppSettings);

        // Register MSAL authentication events
        this.registerAuthenticationEventHandler();
    }

    /**
     * Updates the security stamp for the application
     * @param securityStamp
     */
    updateSecurityStamp(securityStamp: string) {
        SecurityCacheService.cacheSecurityStamp(securityStamp);
        // notify any listeners of the change
        this.securityStampUpdated.next(securityStamp);
    }

    /**
     * Updates the security transactions for the application
     * @param securityTransactions
     */
    updateSecurityTransactions(securityTransactions: SecurityClaim[]) {
        SecurityCacheService.cacheSecurityClaims(securityTransactions);
        // notify any listeners of the change
        this.securityTransactionsUpdated.next(securityTransactions);
    }

    /**
    * Updates the security transactions for the application
    * @param acsTransactions
    */
    updateACSTransactions(acsTransactions: SecurityClaim[]) {
        SecurityCacheService.cacheACSClaims(acsTransactions);
        // notify any listeners of the change
        this.acsTransactionsUpdated.next(acsTransactions);
    }

    /**
    * Updates the multipleClaimSets for the application
    * All allowed claims to this user will need to be cached and all registered subscribers will be notified of this update
    * Subscribees will receive a collection of claim sets applicable to this user
    *@param multipleClaimSets
    */
    updateMultipleClaimSets(securityTransactionClaims: SecurityClaim[], acsTransactionClaims: SecurityClaim[]) {
        SecurityCacheService.cacheSecurityClaims(securityTransactionClaims);
        SecurityCacheService.cacheACSClaims(acsTransactionClaims);
        var multipleClaimSets: SecurityClaim[][] = [securityTransactionClaims, acsTransactionClaims];

        // Notify all registered subscribers of the update
        this.multipleClaimSetsUpdated.next(multipleClaimSets as any);
    }

    /**
     * Updates the business unit for the application
     * @param businessUnitId
     */
    updateBusinessUnit(businessUnitId: string) {
        SecurityCacheService.cacheBusinessUnit(businessUnitId);
        const cachedId: string = this.businessUnitId;
        const cachedName: string = this.businessUnitName;

        const businessUnit = new BusinessUnitModel();

        if (cachedId != undefined && cachedId.length > 0) {
            businessUnit.id = cachedId as any;
        }
        businessUnit.businessUnitName = cachedName;

        // notify any listeners of the change
        this.businessUnitUpdated.next(businessUnit);
    }

    /**
     * Updates the business unit for the application
     * @param notificationsCount
     */
    updateNotificationsCount(notificationsCount: string) {
        // notify any listeners of the change
        this.notificationsCount.next(notificationsCount);
    }

    updateWindowLocalStorage(windowId: string) {
        SecurityCacheService.cacheWindow(windowId);
    }

    /**
     * Navigates the user to a place for selecting the business unit
     */
    selectBusinessUnit() {
        this._router.navigate(['/', 'auth', 'bu']);
    }

    /**
     * Navigates the user to login
     */
    login() {
        SecurityCacheService.clearCacheSecurityClaims();
        SecurityCacheService.clearCacheACSClaims();
        this._msalService.loginRedirect();
    }

    /**
    * Navigates the user to logout
    */
    logOut() {
        SecurityCacheService.clearCacheSecurityStamp();
        SecurityCacheService.clearCacheSecurityClaims();
        SecurityCacheService.clearCacheACSClaims();
        this._msalService.logoutRedirect({
            postLogoutRedirectUri: this.appSettings.security.postLogoutRedirectUri || '/'
        });
    }

    /**
     * Acquires an access token.
     * @returns an observable providing the access token.
     */
    acquireToken(): Observable<string> {
        if (this.isAuthenticated) {
            const requestOption = { scopes: this.scopes };
            return this._msalService.acquireTokenSilent(requestOption)
                .pipe(map((result) => {
                    this.updatedAccessToken = result.accessToken;
                    return result.accessToken;
                }),
                catchError((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        setTimeout(() => {
                            SecurityCacheService.clearCacheSecurityClaims();
                            this.login();
                        }, 2);
                    }
                    return of(error);
                }));
            return of(null);
        } else {
            return of(null);
        }
    }

    initializeAcquireToken(): Observable<string> {
        if (this.isAuthenticated) {
            const requestOption = { scopes: this.scopes };
            return this._msalService.initialize().pipe(mergeMap(() => this.acquireToken()));
        } else {
            return of(null);
        }
    }

    /**
     * Indicates whether the user has a specific security transaction claim
     * @param claimValue
     */
    hasSecurityTransactionClaim(claimValue: SecurityTransactionType | SecurityTransactionType[]) {
        if (claimValue instanceof Array) {
            // check array of claims
            const arrayOfTransactions = claimValue.map((value: any, index: number, array: any) => SecurityTransactionType[value]);
            return this.hasClaim(this.transactionClaim, arrayOfTransactions);
        } else {
            // check specific claim
            const transactionName = SecurityTransactionType[claimValue];
            return this.hasClaim(this.transactionClaim, transactionName);
        }
    }

    /**
    * Indicates whether the user has a specific ACS claim
    * @param claimValue
    */
    hasACSTransactionClaim(claimValue: ACSTransactionType | ACSTransactionType[]): boolean {
        if (claimValue instanceof Array) {
            // check array of claims
            const arrayOfTransactions = claimValue.map((value: any, index: number, array: any) => ACSTransactionType[value]);
            return this.hasACSTransactionClaimEx(this.transactionClaim, arrayOfTransactions);
        } else {
            // check specific claim
            const ACSName = ACSTransactionType[claimValue];
            return this.hasACSTransactionClaimEx(this.transactionClaim, ACSName);
        }
    }

    /**
     * Indicates whether the user has incoming set of security transaction claims
     * @param claimValue
     */
    hasClaim(claimType: string, claimValue: string | string[]): boolean {
        // check the claims coming back from azure as well as from the api server
        let hasClaim = false; // this._adalService.hasClaim(claimType, claimValue);
        if (!hasClaim) {
            // check the api for Security Transaction Claim
            const apiClaims = SecurityCacheService.securityClaims;
            if (apiClaims != undefined) {
                var valuesToSearchFor = claimValue instanceof Array ? claimValue : [claimValue];
                hasClaim = valuesToSearchFor.some(c => apiClaims.some(c2 => c2.type == claimType && c2.value == c));
            }
        }
        return hasClaim;
    }

    /**
     * Indicates whether the user has a incoming set of ACS transaction claim
     * @param claimValue
     */
    hasACSTransactionClaimEx(claimType: string, claimValue: string | string[]): boolean {
        // check the api for ACS Claims
        let hasClaim = false;

        const acsClaims = SecurityCacheService.acsClaims;

        if (undefined != acsClaims) {
            // Create an input array
            var acsValuesToSearchFor = claimValue instanceof Array ? claimValue : [claimValue];
            // We need any possible match (one or more matches) to return true
            hasClaim = acsValuesToSearchFor.some(acsClaimToSearch => acsClaims.some(acsClaim => claimType == acsClaim.type
                && acsClaimToSearch == acsClaim.value));
        }
        return hasClaim;
    }

    /**
     * Indicates whether the user has a incoming combined transaction claims:
     * Currently this includes Security and ACS transaction Claims
     * @param claimValue
     */
    hasClaimMultiple(securityTransactionClaims: string[], acsTransactionClaims: string[]): boolean {
        const hasSecurityTransactionClaims: boolean = this.hasClaim(this.transactionClaim, securityTransactionClaims);
        const hasACSTransactionClaims: boolean = this.hasACSTransactionClaimEx(this.acsTransactionClaim, acsTransactionClaims);

        return (hasSecurityTransactionClaims && hasACSTransactionClaims);
    }

    /*
     * Registers the authentication event handler.
     */
    private registerAuthenticationEventHandler() {
        this._msalBroadcastService.msalSubject$
            .subscribe(
                event => {
                    switch (event.eventType) {
                        case EventType.LOGIN_START:
                            // clear the security claims and set the internal redirect uri
                            SecurityCacheService.clearCacheSecurityClaims();
                            break;
                        case EventType.LOGOUT_SUCCESS:
                            // complete observables and clear cache
                            this.onLogoutSuccess();
                            break;
                        case EventType.LOGIN_SUCCESS:
                        case EventType.SSO_SILENT_SUCCESS:
                            // casy event payload to authentication result
                            let authenticationResult = event.payload as AuthenticationResult;
                            // get account info
                            let account = authenticationResult.account as AccountInfo;
                            if (account) {
                                this._msalService.instance.setActiveAccount(account);
                            }
                            this.startTokenWatchRenewal();
                            this.updatedAccessToken = authenticationResult.accessToken;
                            if (event.eventType === EventType.LOGIN_SUCCESS) {
                                // trigger user details update to avoid guest from being display while authentication process is in progress.
                                this.userUpdated.next(!this.hasApplicationAccess ? '' : this.userName);
                            }
                            break;
                        case EventType.ACQUIRE_TOKEN_SUCCESS:
                        case EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS:
                            // Update the id token if renewed
                            let result = event.payload as AuthenticationResult;
                            if (result) {
                                this.updatedAccessToken = result.accessToken;
                                if (!result.fromCache) {
                                    this.idTokenUpdated.next(result.idToken);
                                }
                            }
                            break;
                        case EventType.LOGIN_FAILURE:
                        case EventType.SSO_SILENT_FAILURE:
                            // handle authentication error
                            this.hasAuthErrors = true;
                            if (event.error) {
                                this.handleAuthenticationError(event.error.message.toUpperCase());
                            }
                            break;
                        case EventType.ACQUIRE_TOKEN_FAILURE:
                        case EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE:
                        case EventType.LOGOUT_FAILURE:
                            this.hasAuthErrors = true;
                            break;
                    }
                },
                error => {
                    this.hasAuthErrors = true;
                });
    }

    /**
    * Handles the authentication error.
    * @param errorMessage: The upper case error message.
    */
    private handleAuthenticationError(errorMessage) {
        if (errorMessage.includes(this._authErrorCodes.registrationCancelled)) {
            this.login();
        } else {
            this.logOut();
        }
    }

    /**
      * Called on logout success.
      */
    private onLogoutSuccess() {
        this.stopTokenWatchRenewal();
        this.idTokenUpdated.next('');
        this.userUpdated.next('');
        SecurityCacheService.clearCacheSecurityStamp();
        this.securityStampUpdated.next('');
        SecurityCacheService.clearCacheSecurityClaims();
        SecurityCacheService.clearCacheACSClaims();
        this.securityTransactionsUpdated.next(null);
    }

    private startTokenWatchRenewal() {
        if (this.isAuthenticated && this._renewTokenIntervalId == -1) {
            const securitySettings = this.appSettings.security;
            let interval = securitySettings.renewTokenInterval;
            this._renewTokenIntervalId = window.setInterval(() => {
                this.acquireToken().subscribe(() => { });
            }, interval);
        }
    }

    private stopTokenWatchRenewal() {
        if (this._renewTokenIntervalId > -1) {
            clearInterval(this._renewTokenIntervalId);
            this._renewTokenIntervalId = -1;
        }
    }
}
